import { Menu, Restaurant } from '@app/types';
import { removeAccentuation } from '@app/utils/strings';
import { useEffect, useRef, useState } from 'react';

type SearchConfig = {
  term: string;
  shouldIgnoreSearch: boolean;
};

export function useSearchOnMenu(menusSource: Menu[], config: SearchConfig) {
  const [isSearching, setIsSearching] = useState(false);
  const [searchIsActived, setSearchIsActived] = useState(false);
  const [searchOnMenuResult, setSearchOnMenuResult] = useState<Menu[]>([]);

  const { term, shouldIgnoreSearch } = config;

  const searchTimeout = useRef<number>();
  useEffect(() => {
    window.clearTimeout(searchTimeout.current);

    if (term.trim() === '' || shouldIgnoreSearch) {
      resetSearchStates();
      return;
    }

    setIsSearching(true);
    searchTimeout.current = window.setTimeout(async () => {
      const menusFound = await searchOnRestaurantMenuByText(term, menusSource);
      setSearchIsActived(true);
      setSearchOnMenuResult(menusFound);
      setIsSearching(false);
    }, 500);

    function resetSearchStates() {
      setSearchIsActived(false);
      setSearchOnMenuResult([]);
      setIsSearching(false);
    }
  }, [term, shouldIgnoreSearch, menusSource]);

  return { searchIsActived, isSearching, searchOnMenuResult };
}

async function searchOnRestaurantMenuByText(term = '', menus: Menu[]): Promise<Menu[]> {
  const termNormalized = removeAccentuation(term.trim().toLowerCase());

  return new Promise((resolve) => {
    const result: Menu[] = [];
    for (const menu of menus) {
      const nameNormalized = removeAccentuation(menu.nome.toLowerCase());
      if (nameNormalized.includes(termNormalized)) {
        result.push(menu);
        break;
      }

      const itemsFound = filterItemsWithTerm(menu);

      if (itemsFound.length > 0) {
        result.push({ ...menu, itens: itemsFound });
      }
    }

    resolve(result);
  });

  function filterItemsWithTerm(menu: Menu) {
    const itemsFound = [];
    for (const item of menu.itens ?? []) {
      const nameNormalized = removeAccentuation(item.nome.toLowerCase());
      if (nameNormalized.includes(termNormalized)) {
        itemsFound.push(item);
        continue;
      }
    }
    return itemsFound;
  }
}
